import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Button,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Select,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { cloneDeep, forEach, get, includes } from "lodash";
import Dropzone from "react-dropzone";
import { Tooltip } from "react-tooltip";
import { acceptedFileTypes } from "../../utils/constants";
import utils from "../../utils/index";
import AdminDocumentTypeList from "../AdminDocumentTypeList";
import { docTypeIcons } from "../documentTypes";
import "./DocumentListRefactor.css";

const StyledPaperPadding = styled(Paper)({
    padding: "0 20px 20px",
    border: "none",
    marginBottom: "20px",
    boxShadow: "0 1px 2px rgba(34, 54, 70, 0.1)",
});

const StyledPaperHeading = styled("h3")({
    fontSize: "18px",
    margin: "14px 0",
});

const StyledHrDivider = styled("hr")({
    margin: "0 -20px",
    borderTop: "1px solid rgba(0, 0, 0, 0.06)",
});

const StyledUploadButton = styled(Button)({
    color: "#757575",
    fill: "#757575",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledApprovedBy = styled("div")({
    fontWeight: 600,
});

const StyledSelect = styled(Select)({
    fontSize: "14px",
    padding: "9px 6px",
    boxSizing: "border-box",
    height: "37px",
    width: "100%",
    "&:focus": {
        backgroundColor: "transparent",
        borderBottom: "none",
    },
    "&::before": {
        borderBottom: "none !important",
    },
    "&::after": {
        borderBottom: "none !important",
    },
});

const StyledMenuCategory = styled(MenuItem)({
    height: "12px",
    fontSize: "11px",
    fontStyle: "italic",
    textTransform: "uppercase",
});

const StyledMenuItem = styled(MenuItem)({
    fontSize: "14px",
    padding: "1vh 2vw",
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "#ebebeb",
    },
});

const StyledDeleteProgress = styled("div")({
    width: "24px",
    padding: "0 10px",
});

const StyledDocIcon = styled("div")({
    float: "left",
});

const StyledDocName = styled("div")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "block",
    textOverflow: "ellipsis",
    paddingTop: "2px",
    paddingLeft: "5px",
    paddingRight: "1vw",
});

const DocumentList = (props) => {
    const {
        allDocumentTypes,
        alternateTitle,
        anchorEl,
        approveDocument,
        copyDocUrl,
        docApproved,
        docMenuKey,
        documentsUploading,
        elementsSaving,
        formDisabled,
        handleAddDocuments,
        handleMenuClose,
        handlePreviewDocument,
        handleOpenDocumentMenu,
        header,
        noHeader,
        open,
        options,
        restrictPreview,
        role,
        savedDocuments,
        selectDocumentType,
        terminationDocument,
        toggleDeleteDialog,
        toggleTerminationDoc,
        type,
        usedDocTypes,
        voucherStatus,
    } = props;

    const handleSelectDocumentType = (key, event) => {
        selectDocumentType(key, event.target.value);
    };

    const handleRemoveDocument = (key, documentType) => {
        toggleDeleteDialog(true, key);

        if (documentType === "TERMINATION_DOCUMENT") {
            toggleTerminationDoc(false);
        }
    };

    const handleApproveDocument = (key) => {
        approveDocument(key);
    };

    const handleCopyDocumentUrl = (key) => {
        copyDocUrl(key);
        handleMenuClose();
    };

    let canView = [];
    let canDelete = [];
    let brokerPaymentDocTypes = cloneDeep(props.documentTypes);
    let documentTypes = cloneDeep(props.documentTypes);
    let disallowOtherOption = false;
    let voucherAwaitingPayment = voucherStatus === "Awaiting Payment" ? true : false;
    let voucherReadyForAccounting = voucherStatus === "Ready For Accounting" ? true : false;
    let dropzoneRef;

    if (options) {
        canView = options.canView;
        canDelete = options.canDelete;
        if (get(options, "canUpload", []).length > 0 && documentTypes !== undefined) {
            forEach(Object.keys(documentTypes), (key) => {
                if (!includes(options.canUpload, key)) {
                    delete documentTypes[key];
                }
            });
        }
    }

    if (role === "broker") {
        forEach(Object.keys(documentTypes), (key) => {
            if (!includes(["OTHER", "COMMISSION_PAYMENT"], key)) {
                delete brokerPaymentDocTypes[key];
            }
        });
    }

    disallowOtherOption = get(options, "disallowOtherOption", false);

    return (
        <StyledPaperPadding>
            <div className="attachedDocumentsUnresponsive">
                <div>
                    <StyledPaperHeading>
                        {noHeader ? "" : alternateTitle ? alternateTitle : "Attached Documents"}
                    </StyledPaperHeading>
                </div>
                <div>
                    <Dropzone
                        accept={acceptedFileTypes}
                        ref={(node) => {
                            dropzoneRef = node;
                        }}
                        onDrop={(accepted, rejected) => {
                            handleAddDocuments(accepted, rejected, type);
                        }}
                        style={{ display: "none" }}
                    >
                        {({ getRootProps, getInputProps }) => <input {...getInputProps()} />}
                    </Dropzone>
                    <StyledUploadButton
                        disabled={formDisabled}
                        component="span"
                        onClick={() => {
                            dropzoneRef.open();
                        }}
                    >
                        <CloudUploadIcon />
                        &nbsp;&nbsp;Upload
                    </StyledUploadButton>
                </div>
            </div>
            <div>
                <StyledHrDivider />
                <div>
                    <div className="attachedDocumentsContainerHeaderRow">
                        <div className="attachedDocumentsContainerHeader">
                            {header.map((x) => (
                                <div className={x.extraHeaderClassName}>
                                    <span className="attachedDocumentsContainerHeaderText">{x.name}</span>
                                </div>
                            ))}
                        </div>
                        <div className="attachedDocumentsContainerHeader hideForSmallerDisplays">
                            {header.map((x) => (
                                <div className={x.extraHeaderClassName}>
                                    <span className="attachedDocumentsContainerHeaderText">{x.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <StyledHrDivider />
                    <div className="attachedDocumentsContainer">
                        {savedDocuments &&
                            Object.keys(savedDocuments).map((x, i) => {
                                const document = savedDocuments[x];
                                if (document["parentEntityType"] && props.parentEntityType) {
                                    if (document["parentEntityType"]["name"] !== props.parentEntityType) {
                                        return null;
                                    }
                                }
                                let removedDocumentType = false;
                                if (
                                    document.mediaAssetType === "TERMINATION_DOCUMENT" &&
                                    get(terminationDocument, "upload", null) === false
                                ) {
                                    toggleTerminationDoc(true);
                                }
                                if (documentTypes !== undefined) {
                                    removedDocumentType = !documentTypes.hasOwnProperty(
                                        get(document, "mediaAssetType")
                                    );
                                }
                                return (
                                    <div key={`doc-${i}`} className="attachedDocument">
                                        <div className="attachmentIconAndNameContainer">
                                            <div
                                                className={
                                                    role === "portlandBrokerAdmin"
                                                        ? "attachmentIconAndNamewApproval"
                                                        : "attachmentIconAndName"
                                                }
                                            >
                                                <StyledDocIcon>
                                                    {get(
                                                        docTypeIcons,
                                                        get(document, "mediaAssetName", " ")
                                                            .split(".")
                                                            .pop()
                                                            .toLowerCase(),
                                                        docTypeIcons["default"]
                                                    )()}
                                                </StyledDocIcon>
                                                <span data-tooltip-id={`doc-${i}`}>
                                                    <StyledDocName>{document["mediaAssetName"]}</StyledDocName>
                                                </span>
                                                <Tooltip id={`doc-${i}`} place="top" type="dark" effect="solid">
                                                    Uploaded on{" "}
                                                    {utils.operations.serializeLocalDateTimeForMediaAsset(
                                                        document.auditCreatedTimestamp
                                                    )}
                                                </Tooltip>
                                            </div>
                                            <div
                                                className={
                                                    role === "portlandBrokerAdmin"
                                                        ? "attachmentDocumentTypewApproval"
                                                        : "attachmentDocumentType"
                                                }
                                            >
                                                <AdminDocumentTypeList
                                                    {...props}
                                                    stateCode={props.stateCode}
                                                    parentEntityType={props.parentEntityType}
                                                    preexistingValue={document}
                                                    style={{
                                                        display: props.adminListReady ? "" : "none",
                                                    }}
                                                    mediaAssetKey={x}
                                                    documentTypesEnumList={allDocumentTypes}
                                                    updateDocumentFromAdminList={props.updateDocumentFromAdminList}
                                                    savedDocuments={savedDocuments}
                                                    voucherStatus={voucherStatus}
                                                    formDisabled={formDisabled}
                                                    options={options}
                                                    role={role}
                                                />
                                                {voucherAwaitingPayment &&
                                                role === "broker" &&
                                                includes(
                                                    ["OTHER", "COMMISSION_PAYMENT"],
                                                    document["mediaAssetType"]
                                                ) ? (
                                                    //adjusted document dropdown on vouchers for brokers to ONLY add commission payments when voucher is Awaiting Payment
                                                    <StyledSelect
                                                        disabled={
                                                            options
                                                                ? !includes(
                                                                      options.canView,
                                                                      document["mediaAssetType"]
                                                                  ) && document["updated"] === true
                                                                : formDisabled
                                                        }
                                                        name={`docType-select-${i}`}
                                                        value={
                                                            document["updated"] !== true
                                                                ? "select"
                                                                : document["mediaAssetType"]
                                                        }
                                                        onChange={(e) => handleSelectDocumentType(x, e)}
                                                        style={{
                                                            display: props.adminListReady ? "none" : "",
                                                        }}
                                                    >
                                                        {documentTypes &&
                                                            Object.keys(brokerPaymentDocTypes).map((r, q) => {
                                                                if (
                                                                    !usedDocTypes.includes(r) &&
                                                                    !brokerPaymentDocTypes[r]["required"] &&
                                                                    !brokerPaymentDocTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else if (r === "OTHER" && disallowOtherOption) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else if (
                                                                    !brokerPaymentDocTypes[r]["required"] &&
                                                                    brokerPaymentDocTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {brokerPaymentDocTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                }
                                                            })}
                                                    </StyledSelect>
                                                ) : (
                                                    <StyledSelect
                                                        disabled={
                                                            (voucherAwaitingPayment || voucherReadyForAccounting) &&
                                                            role === "broker"
                                                                ? true
                                                                : options && formDisabled
                                                                ? !(
                                                                      includes(
                                                                          options.canUpload,
                                                                          document["mediaAssetType"]
                                                                      ) && document["updated"] !== true
                                                                  )
                                                                : formDisabled
                                                        }
                                                        name={`docType-select-${i}`}
                                                        value={
                                                            document["updated"] !== true
                                                                ? "select"
                                                                : removedDocumentType
                                                                ? allDocumentTypes[document["mediaAssetType"]]
                                                                : document["mediaAssetType"]
                                                        }
                                                        onChange={(e) => handleSelectDocumentType(x, e)}
                                                        style={{
                                                            display: props.adminListReady ? "none" : "",
                                                        }}
                                                    >
                                                        <StyledMenuCategory value="" disabled>
                                                            Required
                                                        </StyledMenuCategory>
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((j, k) => {
                                                                if (
                                                                    !usedDocTypes.includes(j) &&
                                                                    documentTypes[j]["required"] &&
                                                                    document["mediaAssetType"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                }
                                                            })}
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((j, k) => {
                                                                if (
                                                                    usedDocTypes.includes(j) &&
                                                                    documentTypes[j]["required"] &&
                                                                    documentTypes[j]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${k}`}
                                                                            value={j}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[j]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                }
                                                            })}
                                                        <Divider />
                                                        <StyledMenuCategory value="" disabled>
                                                            Optional
                                                        </StyledMenuCategory>
                                                        {/* THIS ALLOWS FOR DOCUMENT TYPE TO DISPLAY IF THE TYPE IS NO LONGER ALLOWED */}
                                                        {documentTypes ? (
                                                            <StyledMenuItem
                                                                key={`doc-type-removed-1`}
                                                                value={allDocumentTypes[document["mediaAssetType"]]}
                                                            >
                                                                {allDocumentTypes[document["mediaAssetType"]]
                                                                    ? allDocumentTypes[document["mediaAssetType"]][
                                                                          "display"
                                                                      ]
                                                                    : "Other"}
                                                            </StyledMenuItem>
                                                        ) : (
                                                            ""
                                                        )}
                                                        {documentTypes &&
                                                            Object.keys(documentTypes).map((r, q) => {
                                                                if (
                                                                    !usedDocTypes.includes(r) &&
                                                                    !documentTypes[r]["required"] &&
                                                                    !documentTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else if (r === "OTHER" && disallowOtherOption) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else if (
                                                                    !documentTypes[r]["required"] &&
                                                                    documentTypes[r]["multipleAllowed"]
                                                                ) {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-${i}-${q}`}
                                                                            value={r}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <StyledMenuItem
                                                                            key={`doc-type-required-${i}-${q}`}
                                                                            value={r}
                                                                            style={{ display: "none" }}
                                                                        >
                                                                            {documentTypes[r]["display"]}
                                                                        </StyledMenuItem>
                                                                    );
                                                                }
                                                            })}
                                                    </StyledSelect>
                                                )}
                                            </div>
                                            {role === "portlandBrokerAdmin" && (
                                                <div className="attachedDocumentApprover">
                                                    <StyledApprovedBy>
                                                        {get(document, "approvedBy", "")}
                                                    </StyledApprovedBy>
                                                    <div>
                                                        {utils.operations.formatDate("MM-DD-YYYY")(
                                                            get(document, "approvedByTimestamp", "")
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="attachedDocumentsDropdownOptionsContainer">
                                            <div className="attachedDocumentsDropdownOptions">
                                                {get(elementsSaving, `documents.${x}`) === true ? (
                                                    <StyledDeleteProgress>
                                                        <CircularProgress size={20}></CircularProgress>
                                                    </StyledDeleteProgress>
                                                ) : (
                                                    <IconButton
                                                        style={{ display: "flex" }}
                                                        disabled={
                                                            restrictPreview
                                                                ? true
                                                                : options
                                                                ? includes(canView, document["mediaAssetType"]) ||
                                                                  includes(canDelete, document["mediaAssetType"])
                                                                    ? false
                                                                    : true
                                                                : false
                                                        }
                                                        aria-label="More"
                                                        aria-owns={
                                                            Boolean(document["anchorEl"]) ? `long-menu-${x}-${i}` : null
                                                        }
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleOpenDocumentMenu(x, e.currentTarget)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                )}
                                                <Menu
                                                    id={`long-menu-${x}-${i}`}
                                                    anchorEl={anchorEl}
                                                    open={open}
                                                    onClose={handleMenuClose}
                                                >
                                                    {(!options ||
                                                        includes(
                                                            canView,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() =>
                                                                handlePreviewDocument(
                                                                    get(document, "url", null),
                                                                    docMenuKey
                                                                )
                                                            }
                                                        >
                                                            Open in a new tab
                                                        </StyledMenuItem>
                                                    )}
                                                    {(!options ||
                                                        includes(
                                                            canView,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() => handleCopyDocumentUrl(docMenuKey)}
                                                        >
                                                            Copy Link
                                                        </StyledMenuItem>
                                                    )}
                                                    {role === "portlandBrokerAdmin" && (
                                                        <StyledMenuItem
                                                            id={`doc-approve-${x}-${i}`}
                                                            disabled={docApproved}
                                                            onClick={() => handleApproveDocument(docMenuKey)}
                                                        >
                                                            Approve
                                                        </StyledMenuItem>
                                                    )}
                                                    {(!options ||
                                                        includes(
                                                            canDelete,
                                                            get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                        )) && (
                                                        <StyledMenuItem
                                                            onClick={() =>
                                                                handleRemoveDocument(
                                                                    docMenuKey,
                                                                    get(savedDocuments, `${docMenuKey}.mediaAssetType`)
                                                                )
                                                            }
                                                        >
                                                            Delete
                                                        </StyledMenuItem>
                                                    )}
                                                </Menu>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                        {documentsUploading
                            ? Object.keys(documentsUploading).map((x) => {
                                  if (get(documentsUploading, x)) {
                                      return (
                                          <div className="uploadProgressBar">
                                              <LinearProgress style={{ width: "55%", marginRight: "2vw" }} />
                                          </div>
                                      );
                                  } else {
                                      return null;
                                  }
                              })
                            : null}
                    </div>
                </div>
            </div>
        </StyledPaperPadding>
    );
};

export default DocumentList;
